import Controller from '@ember/controller';
import RouterService from '@ember/routing/router-service';
import { later } from '@ember/runloop';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import TopNavbarAdminComponent from 'donor-elf-web/components/top-navbar-admin';
import TopNavbarCoachingComponent from 'donor-elf-web/components/top-navbar-coaching';
import TopNavbarFundComponent from 'donor-elf-web/components/top-navbar-fund';
import TopNavbarFundsIndexComponent from 'donor-elf-web/components/top-navbar-funds-index';
import TopNavbarAllFundsComponent from 'donor-elf-web/components/top-navbar/all-funds';
import ENV from 'donor-elf-web/config/environment';
import type ContactModel from 'donor-elf-web/models/contact';
import AbilitiesService from 'donor-elf-web/services/abilities';
import AddSomethingModalService from 'donor-elf-web/services/add-something-modal';
import ContactsCacheService from 'donor-elf-web/services/contacts-cache';
import CurrentUserService from 'donor-elf-web/services/current-user';
import CustomReportsService from 'donor-elf-web/services/custom-reports';
import FundGoalService from 'donor-elf-web/services/fund-goal-service';
import FundsService from 'donor-elf-web/services/funds-service';
import NavbarSettingsService from 'donor-elf-web/services/navbar-settings';
import SessionService from 'donor-elf-web/services/session';
import SettingsService from 'donor-elf-web/services/settings';
import FlashMessagesService from 'ember-cli-flash/services/flash-messages';

const componentMap: Record<string, any> = {
  'top-navbar-admin': TopNavbarAdminComponent,
  'top-navbar-coaching': TopNavbarCoachingComponent,
  'top-navbar-fund': TopNavbarFundComponent,
  'top-navbar-funds-index': TopNavbarFundsIndexComponent,
  'top-navbar/all-funds': TopNavbarAllFundsComponent,
};

export default class ApplicationController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare addSomethingModal: AddSomethingModalService;
  @service declare contactsCache: ContactsCacheService;
  @service declare currentUser: CurrentUserService;
  @service declare customReports: CustomReportsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare fundGoalService: FundGoalService;
  @service declare fundsService: FundsService;
  @service declare navbarSettings: NavbarSettingsService;
  @service declare media: any;
  @service declare router: RouterService;
  @service declare session: SessionService;
  @service declare settings: SettingsService;

  queryParams = ['loginToken', 'loginEmail'];
  @tracked loginToken?: string;
  @tracked loginEmail?: string;

  get apiUrl() {
    return ENV.apiUrl;
  }

  get currentMenuLabel() {
    return this.navbarSettings.currentMenuLabel;
  }

  get canEditContact() {
    return this.abilities.abilities['edit_contact'];
  }

  get showTopNavbar() {
    return this.media.isMediumUp;
  }

  get canViewAdminArea() {
    return this.abilities.abilities['view_admin_area'];
  }

  get canViewClassicSite() {
    return false; //alias('abilities.abilities.view_classic_site'),
  }

  get hasYtdGoalType() {
    return this.fundGoalService.hasYtdGoalType;
  }

  get hasOmsMonthlySummaryCustomReport() {
    return this.settings.fundSettings?.customReports?.includes(
      'oms-monthly-summary',
    );
  }

  get topNavbarComponent() {
    return componentMap[this.navbarSettings.topNavbarComponent] || null;
  }

  handleContactSelected = (contact: ContactModel) => {
    this.router.transitionTo('funds.fund.contacts.contact', contact.id);
  };

  navbarBrandClicked = () => {
    if (this.session?.isAuthenticated) {
      if (this.fundsService.selectedFund) {
        this.router.transitionTo(
          'funds.fund.dashboard',
          this.fundsService.selectedFund.id,
        );
      } else {
        this.router.transitionTo('funds.fund', 'selected-fund');
      }
    }
  };

  changeMenuLabelTo = (menuLabel: string, e: Event) => {
    this.navbarSettings.currentMenuLabel = menuLabel;
    e.preventDefault();
    return false;
  };

  handleMenuClosed = () => {
    later(() => {
      this.navbarSettings.resetCurrentMenuLabel();
      // I know this is a hack doing DOM manipulation in a controller, but it shouldn't
      // have any side effects and it's not worth creating a component
      const bmMenu = document.querySelector('.bm-menu');
      if (bmMenu) {
        bmMenu.scrollTop = 0;
      }
    }, 500);
  };

  templateDidInsert = () => {
    this.loginToken = undefined;
    this.loginEmail = undefined;
  };
}
